import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { graphql_call as graphql_admin_call } from '../../schemas/server_admin_query'

import {
  query_external_financial_account,
  query_external_financial_accounts,
} from './external_financial_graphql'

export type ExternalFinancialAccount = {
  id: string
  name: string
  current_balance: number
  available_balance: number
  currency: string
  custodian: string
  updated_at: string
  updated_by: string
  fund_id: string | null
}

export type ExternalFinancialAccountsResponse = {
  external_financial_accounts: {
    nodes: ExternalFinancialAccount[]
  }
}

type ExternalFinancialAccountsRequest = {
  ids?: string[]
  refetchInterval?: number
}

export function useExternalFinancialAccounts({
  ids,
  refetchInterval,
}: ExternalFinancialAccountsRequest): UseQueryResult<
  ExternalFinancialAccount[]
> {
  return useQuery({
    queryKey: ['external_financial_accounts', ids],
    queryFn: async (): Promise<ExternalFinancialAccount[]> => {
      try {
        const response = await graphql_admin_call({
          query: query_external_financial_accounts,
          variables: ids ? { ids } : {},
        })
        return response.nodes
      } catch (error) {
        console.error('Error fetching external financial accounts:', error)
        return []
      }
    },
    refetchInterval,
    enabled: true,
  })
}

type ExternalFinancialAccountRequest = {
  id: string
  refetchInterval?: number
}

export function useExternalFinancialAccount({
  id,
  refetchInterval,
}: ExternalFinancialAccountRequest): UseQueryResult<ExternalFinancialAccount | null> {
  return useQuery({
    queryKey: ['external_financial_account', id],
    queryFn: async (): Promise<ExternalFinancialAccount | null> => {
      try {
        const response = await graphql_admin_call({
          query: query_external_financial_account,
          variables: { id },
        })
        return response as ExternalFinancialAccount | null
      } catch (error) {
        console.error('Error fetching external financial account:', error)
        return null
      }
    },
    refetchInterval,
    enabled: !!id,
  })
}
